import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabaseClient';
import { useConfig } from '../../context/ConfigContext';
import AdminHeader from '../../components/admin/AdminHeader';
import ConfigEditor from './ConfigEditor';
import Icon from 'components/AppIcon';

const AdminDashboard = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { refetchConfig } = useConfig();

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('calculation_config')
        .select('*')
        .eq('id', 1)
        .single();

      if (error) throw error;

      setConfig(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching config:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConfigUpdate = (updatedConfig) => {
    setConfig(updatedConfig);
    // Refetch config in the main app context so changes reflect immediately
    refetchConfig();
  };

  return (
    <div className="min-h-screen bg-surface-50">
      <AdminHeader />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <Icon name="Settings" size={24} className="text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-heading-bold text-text-primary">
                Configuration Management
              </h1>
              <p className="text-text-secondary">
                Update calculation parameters for the FiberFlow calculator
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="bg-surface rounded-xl shadow-elevation-2 border border-border">
          <div className="p-6 border-b border-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Icon name="Database" size={20} className="text-primary" />
                <h2 className="text-xl font-heading-semibold text-text-primary">
                  Calculation Parameters
                </h2>
              </div>
              <button
                onClick={fetchConfig}
                disabled={loading}
                className="inline-flex items-center px-3 py-2 text-sm text-text-secondary hover:text-primary hover:bg-primary-50 rounded-lg transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Icon name="RefreshCw" size={16} className="mr-1.5" />
                Refresh
              </button>
            </div>
          </div>

          <div className="p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-text-secondary">Loading configuration...</p>
                </div>
              </div>
            ) : error ? (
              <div className="flex items-center space-x-2 p-4 bg-error-50 border border-error-200 rounded-lg text-error">
                <Icon name="AlertCircle" size={20} />
                <span className="text-sm font-body-medium">{error}</span>
              </div>
            ) : (
              <ConfigEditor config={config} onUpdate={handleConfigUpdate} />
            )}
          </div>
        </div>

        {/* Info Card */}
        <div className="mt-6 bg-info-50 border border-info-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <div className="w-6 h-6 bg-info-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <Icon name="Info" size={14} className="text-info" />
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-body-medium text-info mb-1">
                Configuration Updates
              </h4>
              <p className="text-xs text-text-secondary">
                Changes to these values will take effect immediately for all users accessing the calculator.
                The configuration is stored in the Supabase database and cached by the application.
              </p>
            </div>
          </div>
        </div>

        {/* Last Updated Info */}
        {config?.last_updated_at && (
          <div className="mt-4 text-center">
            <p className="text-xs text-text-tertiary">
              Last updated: {new Date(config.last_updated_at).toLocaleString()}
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
